// render(): the non-visual part of the reference tool's render() — applies everything the HTML auto-writes into the
// form (select lists + fallbacks, material densities, CLT geometry, ε auto, distances/spacings, γM, rows/cols linked
// to the maximum, visibility, warnings), runs compute() and calcBlockReason(), formats the result cards.

import { currentAlphaElem1, currentAlphaElem2, currentFa, currentFn, currentFv, fmtN } from "./angles";
import type { EngineCatalog } from "./catalog";
import { tryCurrentClt } from "./clt";
import { compute } from "./compute";
import { inputPatch, toControlValues, type ControlValues } from "./controls";
import { createCtx, isSteelMat, type EngineCtx, type EngineOptions, type EngineTexts } from "./ctx";
import {
  cltFitsMinThickness,
  currentWasherH,
  enforcePredrillByRule,
  populateDiametri,
  populateFamiglie,
  populateLunghezze,
  populateRondelle,
  PARTIAL_THREAD_A_MARGIN_MM,
  PARTIAL_THREAD_LG1_EXCESS_MM,
  screwFitsLvlMinThickness,
  screwFitsMinMemberSize,
  screwFitsMinThicknessRule,
  screwFitsPartialThread,
  screwFitsPenetrationDepth,
  screwLengthFitsElements,
  tipMarginMm,
  tLabels,
} from "./eligibility";
import {
  applyMaxRowsCols,
  autoFillGammaM,
  autoFillSpacing,
  toggleGammaMEditable,
  toggleSpacingEditable,
  updateGammaMColonnaVisibility,
  updateLengthWarnings,
  type MaxRowsCols,
} from "./layout";
import {
  applyMatClasse,
  materialDefaults,
  populateMatClasse,
  populateMatSpecie,
  populateMatTipo,
  updateMatSpecieVisibility,
  updateMatWarnings,
  type ElementN,
} from "./materials";
import { governingLabel } from "./resistance";
import type { CalcState } from "./state";
import type { CalculationInput } from "./types";
import type { UiResults, UiState } from "./ui";
import { updateThreeLabels, updateVisibility } from "./visibility";

// ---- calcBlockReason --------------------------------------------------------------------------------------------

// Screw compatibility with thicknesses/materials ("even by hand it must not be allowed if it breaks the compatibility
// rule"): same filters as the family/diameter/length menus. Manual mode only.
export function screwCompatibilityIssue(ctx: EngineCtx, state: CalcState): string | null {
  const tb = ctx.t.engine.block;
  const rec = state.screwRec;
  if (!rec) return tb.noScrew;
  const fam = rec.family,
    d = rec.geometria_mm.d1,
    L = rec.geometria_mm.L,
    LG1 = rec.geometria_mm.LG1;
  const t1 = state.tHead,
    t2 = state.tPoint; // (head side, tip side)
  const [lt1, lt2] = tLabels(ctx);
  const wh = currentWasherH(ctx);
  if (!screwLengthFitsElements(ctx, L, t1, t2, d))
    return tb.length(
      L,
      `${lt1}+4d < L${wh > 0 ? "−h" : ""} < ${lt1}+${lt2}−${tipMarginMm(ctx, d).toFixed(0)}`,
      `${(t1 + 4 * d + wh).toFixed(0)} < L < ${(t1 + t2 - tipMarginMm(ctx, d) + wh).toFixed(0)} mm`,
      wh,
    );
  if (rec.threadType === "partial" && !screwFitsPartialThread(ctx, fam, d, L)) {
    return tb.partialThread(
      `4d < LG1 ≤ ${lt2}+${PARTIAL_THREAD_LG1_EXCESS_MM} mm`,
      ctx.partialAStrict
        ? { rule: `A = L−LG1 ≥ ${lt1}−${PARTIAL_THREAD_A_MARGIN_MM} mm`, values: `A=${(L - LG1).toFixed(0)} mm, ${lt1}=${t1.toFixed(0)}` }
        : null,
      `LG1=${LG1} mm, ${lt2}=${t2.toFixed(0)}, 4d=${(4 * d).toFixed(0)}`,
    );
  }
  if (!screwFitsPenetrationDepth(ctx, fam, d, L)) return tb.penetrationRule(`${fam} Ø${d} × ${L} mm`);
  const fitsMin =
    screwFitsMinMemberSize(ctx, fam, d) &&
    screwFitsMinThicknessRule(ctx, fam, d) &&
    screwFitsLvlMinThickness(ctx, fam, d) &&
    (isSteelMat(ctx.v.materiale1) || cltFitsMinThickness(tryCurrentClt(ctx, 1), d)) &&
    cltFitsMinThickness(tryCurrentClt(ctx, 2), d);
  if (!fitsMin) return tb.minSize(`${fam} Ø${d}`);
  return null;
}

// Numeric inputs that start EMPTY (to avoid compilation errors): while one is empty the calculation is not run.
// Disabled (auto-filled) or hidden fields are ignored.
export const REQUIRED_INPUTS: [keyof ControlValues, string][] = [
  ["thetaNum", "θ"],
  ["b1", "b1"],
  ["d1", "d1"],
  ["spessore1", "t1"],
  ["b2", "b2"],
  ["d2", "d2"],
  ["spessore2", "t2"],
  ["forceFn", "Fn"],
  ["forceFv", "Fv"],
  ["forceFa", "Fa"],
];
function inputHiddenOrDisabled(ctx: EngineCtx, id: keyof ControlValues): boolean {
  const { visible, disabled } = ctx.ui;
  switch (id) {
    case "d1":
      return !visible.d1Field;
    case "d2":
      return !visible.d2Field || disabled.d2;
    case "b2":
      return disabled.b2;
    case "spessore1":
      return disabled.spessore1;
    case "spessore2":
      return disabled.spessore2;
    default:
      return false;
  }
}
export function requiredEmptyFields(ctx: EngineCtx): [keyof ControlValues, string][] {
  return REQUIRED_INPUTS.filter(([id]) => {
    if (inputHiddenOrDisabled(ctx, id)) return false;
    return !Number.isFinite(ctx.v[id] as number);
  });
}
// Reason why the calculation is NOT run (results/report hidden), or null. ignoreMode = solution search (always
// discards screws with insufficient penetration, compatibility already guaranteed by the filters).
export function calcBlockReason(ctx: EngineCtx, state: CalcState, ignoreMode?: boolean): string | null {
  const tb = ctx.t.engine.block;
  const vuoti = requiredEmptyFields(ctx);
  if (vuoti.length) return tb.emptyFields(`${ctx.t.shell.toolsGroup} › ${ctx.t.shell.tutorial}`);
  if (state.panelIssue) return state.panelIssue;
  if (!ctx.manualInputMode && !ignoreMode) return null;
  if (!ignoreMode && Number.isFinite(state.tHead) && Number.isFinite(state.tPoint)) {
    const issue = screwCompatibilityIssue(ctx, state);
    if (issue) return issue;
  }
  if (typeof state.screwLEff !== "number" || !Number.isFinite(state.screwLEff) || !Number.isFinite(state.tHead) || !Number.isFinite(state.d))
    return null;
  const pen = state.screwLEff - state.tHead;
  const clt2 = state.cltPoint;
  const [lt1] = tLabels(ctx),
    ptN = state.threeD ? 3 : state.three ? 1 : 2;
  if (clt2 && clt2.latoInserimento === "narrow") {
    const min = 10 * state.d;
    if (pen >= min - 1e-6) return null;
    return tb.cltNarrowPenetration(lt1, pen.toFixed(1), min.toFixed(1), (state.tHead + min).toFixed(1));
  }
  const min = 4 * state.d;
  if (state.screwLEff > state.tHead + min) return null;
  return tb.penetration(ptN, lt1, pen.toFixed(1), min.toFixed(1), (state.tHead + min).toFixed(1));
}

// ---- input → controls (the HTML start state / event cascades a stateless engine must replay) --------------------

// Material selects of element n: list fallbacks (populateMatTipo/Specie/Classe); a new calculation starts with the
// first type/species and class C24 (HTML boot). When the material falls back, the DB densities are applied
// (applyMatClasse, as the HTML change handlers do); persisted densities left null take the DB values (HTML start
// state fills them from the class).
function syncMaterial(ctx: EngineCtx, n: ElementN, materialId: string | null): void {
  const v = ctx.v;
  const known = !!materialId && ctx.cat.materialById.has(materialId);
  const before = [v[`mat${n}Tipo` as const], v[`mat${n}Specie` as const], v[`mat${n}Classe` as const]].join("|");
  populateMatTipo(ctx, n);
  populateMatSpecie(ctx, n);
  populateMatClasse(ctx, n);
  if (!known && ctx.options.matClasse[n].includes("C24")) v[`mat${n}Classe` as const] = "C24";
  const after = [v[`mat${n}Tipo` as const], v[`mat${n}Specie` as const], v[`mat${n}Classe` as const]].join("|");
  if (!known || before !== after) {
    applyMatClasse(ctx, n);
  } else {
    const def = materialDefaults(ctx.cat, v[`mat${n}Tipo` as const], v[`mat${n}Specie` as const], v[`mat${n}Classe` as const], v[`spessore${n}` as const]);
    if (def) {
      if (!Number.isFinite(v[`rhok${n}` as const]) && def.rhok != null) v[`rhok${n}` as const] = def.rhok;
      if (!Number.isFinite(v[`rhomean${n}` as const]) && def.rhomean != null) v[`rhomean${n}` as const] = def.rhomean;
    }
  }
  updateMatWarnings(ctx, n);
  updateMatSpecieVisibility(ctx, n);
}

export type RunEngineOptions = {
  texts: EngineTexts;
  // true only inside the solution search (washer height ignored by the length filters)
  solutionsRunning?: boolean;
  // HTML manualInputMode: pass false right after applying a solution ("Usa questa") until the user edits a field
  manualInputMode?: boolean;
};

export type RenderedCtx = { ctx: EngineCtx; maxRowsCols: MaxRowsCols | null; state: CalcState | null };

// Builds the controls from the input and replays the HTML auto-writes, then compute().
export function renderCtx(input: CalculationInput, catalog: EngineCatalog, opts: RunEngineOptions): RenderedCtx {
  const ctx = createCtx(toControlValues(input, catalog), catalog, opts.texts);
  ctx.solutionsRunning = !!opts.solutionsRunning;
  ctx.manualInputMode = opts.manualInputMode ?? true;

  // materials (element 1 first: in legno3 the element 2 list depends on element 1's CLT choice)
  syncMaterial(ctx, 1, input.element1TimberMaterialId);
  syncMaterial(ctx, 2, input.element2TimberMaterialId);
  toggleSpacingEditable(ctx);
  toggleGammaMEditable(ctx);
  updateGammaMColonnaVisibility(ctx);

  // cascade of the change handlers (visibility first: CLT-derived b2/d2/t/ρk feed the screw filters); the hole type
  // forced by the ETA rule too (in the HTML the select is already forced by the previous render)
  updateVisibility(ctx);
  enforcePredrillByRule(ctx);
  populateFamiglie(ctx);
  populateDiametri(ctx);
  populateLunghezze(ctx);
  populateRondelle(ctx);

  // render()
  enforcePredrillByRule(ctx); // before everything that reads the hole type (the family may have fallen back)
  updateThreeLabels(ctx);
  updateVisibility(ctx);
  populateDiametri(ctx);
  populateLunghezze(ctx);
  populateRondelle(ctx);
  autoFillSpacing(ctx);
  if (ctx.v.gammaMAuto) autoFillGammaM(ctx);
  updateLengthWarnings(ctx);
  const maxRowsCols = applyMaxRowsCols(ctx);

  let state: CalcState | null = null;
  try {
    state = compute(ctx);
  } catch {
    state = null;
  }
  return { ctx, maxRowsCols, state };
}

// ---- output -----------------------------------------------------------------------------------------------------

export type MaxScrewsInfo =
  | { total: 0; html: string }
  | {
      total: number;
      rows: number;
      cols: number;
      passoAsse1: number;
      passoAsse2: number;
      offsetX: number;
      offsetY: number;
      hasOffset: boolean;
      cornerDistanceCapped: boolean;
      maxCornerDistanceRadente: number | null;
      capped: boolean;
      html: string; // #maxScrewsResult content without the "Applica righe/colonne" button
    };

// "Calcola numero massimo di viti" (#maxScrewsBtn → #maxScrewsResult).
export function maxScrewsInfo(best: MaxRowsCols | null, t: EngineTexts): MaxScrewsInfo | null {
  if (!best) return null;
  const tm = t.engine.maxScrews;
  if (best.total === 0) return { total: 0, html: tm.none };
  const hasOffset = Math.abs(best.offsetX) > 1e-6 || Math.abs(best.offsetY) > 1e-6;
  const amber = (text: string) => `<br><span style="color:var(--tc-amber);">${text}</span>`;
  const html =
    tm.max(best.rows, best.cols, best.total) +
    tm.spacing(best.passoAsse1.toFixed(1), best.passoAsse2.toFixed(1)) +
    (hasOffset ? "<br>" + tm.offset(best.offsetX.toFixed(1), best.offsetY.toFixed(1)) : "") +
    (best.cornerDistanceCapped ? amber(tm.cornerCapped(best.rows, best.cols, best.maxCornerDistanceRadente!.toFixed(1))) : "") +
    (best.capped ? amber(tm.capped) : "");
  return {
    total: best.total,
    rows: best.rows,
    cols: best.cols,
    passoAsse1: best.passoAsse1,
    passoAsse2: best.passoAsse2,
    offsetX: best.offsetX,
    offsetY: best.offsetY,
    hasOffset,
    cornerDistanceCapped: best.cornerDistanceCapped,
    maxCornerDistanceRadente: best.maxCornerDistanceRadente,
    capped: best.capped,
    html,
  };
}

export type EngineOutput = {
  values: ControlValues; // effective control values (what the HTML form shows after render)
  options: EngineOptions; // select option lists
  ui: UiState; // visibility/disabled flags, warnings, labels, formatted result cards
  maxRowsCols: MaxRowsCols | null; // computeMaxRowsCols (also the "Calcola numero massimo di viti" result)
  maxScrews: MaxScrewsInfo | null; // #maxScrewsResult data
  state: CalcState | null; // compute() result (+ blocked)
  blockReason: string | null; // calcBlockReason: when set, results/report/violations are not shown
  patch: Partial<CalculationInput>; // input fields to write back so the persisted calculation matches `values`
};

// Post-compute part of render(): notes/warnings and result cards.
function renderResults(ctx: EngineCtx, state: CalcState): void {
  const { visible, texts, labels, textTone } = ctx.ui;
  const tr = ctx.t.engine.results;
  const stats = ctx.t.stats;
  // a check ratio with its outcome
  const percent = (eta: number, ok: boolean) => (eta * 100).toFixed(0) + "%<small> " + (ok ? stats.verified : stats.notVerified) + "</small>";
  texts.spreadNote = state.spreadActive
    ? tr.spreadNote(state.s11.toFixed(0), state.s12.toFixed(0), state.s11Min.toFixed(0), state.s12Min.toFixed(0))
    : null;
  // My,k missing in the screw DB: non-blocking warning (only Johansen modes a/b/c considered)
  visible.etaMyKWarn = !state.screwRec || typeof state.screwRec.resistenza.MyK_Nmm !== "number";

  // two-sided connections: tips overlapping in the central member, staggered side 3
  {
    const stg = !!(state.twoSides && state.stagger && state.staggerDir);
    const ovl = !!(state.twoSides && state.tipOverlap && !state.stagger);
    const lim = state.steel3 ? "t1 + t2/2" : "t2 + t1/2";
    const limit = `${lim} = ${fmtN(state.tHead + state.tPoint / 2, 0)}`;
    texts.staggerNote = stg
      ? tr.staggered(fmtN(state.screwLEff, 0), limit, fmtN(state.d, 1), state.staggerDir as string, state.steel3 ? 1 : 2)
      : ovl
        ? tr.tipsOverlap(
            state.steel3,
            fmtN(state.screwLEff, 0),
            limit,
            fmtN(state.tPoint - ((state.screwLEff as number) - state.tHead), 0),
            fmtN(4 * state.d, 0),
            ctx.t.sections.grid,
          )
        : null;
  }

  // screw length: (t1+4d) < L−h < (t1+t2−margin); CLT narrow tip member: penetration ≥ 10·d. Warning only.
  {
    const screwL = state.screwL as number;
    const screwLEff = state.screwLEff;
    const [lt1, lt2] = tLabels(ctx);
    const margin = tipMarginMm(ctx, state.d);
    const lMax = state.tHead + state.tPoint - margin;
    const cltNarrow2 = !!(state.cltPoint && state.cltPoint.latoInserimento === "narrow");
    const penMinD = cltNarrow2 ? 10 : 4;
    const lMin = state.tHead + penMinD * state.d;
    const rondellaNote = state.rondellaH > 0 ? tr.washerNet(state.rondellaH.toFixed(1), (screwLEff as number).toFixed(0)) : "";
    if (typeof screwLEff === "number" && Number.isFinite(screwLEff) && screwLEff >= lMax) {
      texts.screwLengthWarn = tr.screwTooLong(
        screwL.toFixed(0) + " mm" + rondellaNote,
        `${lt1}+${lt2}−${margin.toFixed(0)} mm = ${lMax.toFixed(1)} mm`,
        margin.toFixed(0),
        state.twoSides,
      );
    } else if (
      ctx.manualInputMode &&
      typeof screwLEff === "number" &&
      Number.isFinite(screwLEff) &&
      (cltNarrow2 ? screwLEff < lMin - 1e-6 : screwLEff <= lMin)
    ) {
      texts.screwLengthWarn = cltNarrow2
        ? tr.screwTooShortClt(
            screwL.toFixed(0) + " mm" + rondellaNote,
            `L−${lt1} = ${(screwLEff - state.tHead).toFixed(1)} mm`,
            (10 * state.d).toFixed(1),
            `L ≥ ${lt1}+10d = ${lMin.toFixed(1)} mm`,
          )
        : tr.screwTooShort(screwL.toFixed(0) + " mm" + rondellaNote, `L > ${lt1}+4d = ${lMin.toFixed(1)} mm`, state.threeD ? 3 : state.three ? 1 : 2);
    } else {
      texts.screwLengthWarn = null;
    }
  }

  // CLT layup info (and the ETA Tab. B1 minimum panel thickness for the narrow side)
  ([
    [1, state.clt1],
    [2, state.clt2],
  ] as const).forEach(([n, clt]) => {
    const key = n === 1 ? "clt1Info" : "clt2Info";
    if (!clt) {
      texts[key] = "";
      return;
    }
    let txt = tr.cltInfo(clt.spessoreTotale.toFixed(0), String(clt.rhok), clt.classe);
    textTone[key] = "gray";
    if (clt.latoInserimento === "narrow") {
      const soglia = 10 * state.d;
      if (clt.spessoreTotale < soglia) {
        txt += tr.cltNarrowThin(soglia.toFixed(1));
        textTone[key] = "amber";
      }
    }
    texts[key] = txt;
  });

  // result cards (formulas next to them: report module)
  const results: UiResults = {
    status: "ok",
    statPill: { labelHtml: "", valueHtml: "", ok: null, formula: null },
    statFvRkVite: (state.resistenza.FvRk / 1000).toFixed(2) + "<small>kN · " + governingLabel(ctx.t, state.resistenza) + "</small>",
    statNef:
      state.nef.toFixed(2) + " <small>" + stats.admissibleScrews(state.viteAmmissibili, state.viteTotali, state.twoSides) + "</small>",
    statNefShear: state.nefShear.toFixed(2),
    lblFvRkUnione: stats.fvRkUnion,
    statFvRkUnione: (state.FvRkConnessione / 1000).toFixed(2) + "<small>" + stats.totalKn + "</small>",
    kmodCard:
      state.faKN > 0
        ? { labelHtml: stats.faxRd, valueHtml: (state.FaxRdUnione / 1000).toFixed(2) + "<small>kN</small>", formula: "formulaFaxRdUnione" }
        : { labelHtml: stats.kmodGammaM, valueHtml: state.kmod.toFixed(2) + " · " + state.gammaM.toFixed(2), formula: null },
    statForceF: state.forceF_kN.toFixed(1) + "<small>kN</small>",
    statFvRdUnione: (state.FvRdUnione / 1000).toFixed(1) + "<small>kN</small>",
    statKser: state.kser != null ? (state.kser / 1000).toFixed(2) + "<small>kN/mm</small>" : stats.notAvailable,
    statVerifica: { valueHtml: "", ok: true },
    statVerificaAx: null,
    statVerificaSplit: null,
  };
  // "Layout viti" card, replaced by the combined load check (EC5 8.28) when Fa > 0
  if (state.faKN > 0) {
    const okComb = (state.etaCombinata as number) <= 1;
    results.statPill = {
      labelHtml: stats.combined,
      valueHtml: percent(state.etaCombinata as number, okComb),
      ok: okComb,
      formula: "formulaCaricoCombinato",
    };
  } else {
    const allOk = state.grid.every((p) => p.ok) && (!state.gridB || state.gridB.every((p) => p.ok));
    results.statPill = { labelHtml: stats.layout, valueHtml: allOk ? stats.layoutOk : stats.layoutBad, ok: allOk, formula: null };
  }
  labels.alpha1Display = currentAlphaElem1(ctx).toFixed(1) + "°";
  labels.alpha2Display = currentAlphaElem2(ctx).toFixed(1) + "°";
  labels.edDisplay = Math.hypot(currentFn(ctx), currentFv(ctx)).toFixed(1);
  labels.faDisplay = currentFa(ctx).toFixed(1);

  const verificaOk = state.eta <= 1;
  const showSplit = (state.three || state.steel3) && state.etaSplitting != null && Number.isFinite(state.etaSplitting);
  const splitOk = !showSplit || (state.etaSplitting as number) <= 1;
  visible.statVerificaSplitRow = showSplit;
  results.statVerificaSplit = showSplit ? percent(state.etaSplitting as number, splitOk) : null;
  results.statVerifica = {
    valueHtml: percent(state.eta, verificaOk),
    ok: verificaOk && splitOk,
  };
  if (state.faKN > 0) {
    const okAxSimple = (state.etaAxSimple as number) <= 1;
    visible.statVerificaAxRow = true;
    results.statVerificaAx = percent(state.etaAxSimple as number, okAxSimple);
  } else {
    visible.statVerificaAxRow = false;
  }

  // calculation not run (calcBlockReason): grid list hidden, status "In attesa dei dati"
  state.blocked = calcBlockReason(ctx, state);
  const bad = state.grid
    .filter((p) => !p.ok)
    .concat(
      state.gridB
        ? state.gridB.filter((p) => !p.ok).map((p) => ({ ...p, violations: p.violations.map((x) => ctx.t.engine.violations.side3(x)) }))
        : [],
    );
  if (state.blocked) {
    visible.violationsCard = false;
    ctx.ui.violations = null;
    texts.calcBlockedBanner = ctx.t.violations.blockedPrefix + state.blocked;
    results.statPill = { ...results.statPill, valueHtml: "—", ok: null };
    results.status = "waiting";
  } else {
    visible.violationsCard = true;
    ctx.ui.violations = bad.map((p) => ({ x: p.x, y: p.y, violations: p.violations }));
    texts.calcBlockedBanner = null;
    results.status = results.statPill.ok === false || !results.statVerifica.ok ? "bad" : "ok";
  }
  ctx.ui.results = results;
}

// Runs the engine for a calculation input (see EngineOutput).
export function runEngine(input: CalculationInput, catalog: EngineCatalog, opts: RunEngineOptions): EngineOutput {
  const { ctx, maxRowsCols, state } = renderCtx(input, catalog, opts);
  if (state) renderResults(ctx, state);
  return {
    values: ctx.v,
    options: ctx.options,
    ui: ctx.ui,
    maxRowsCols,
    maxScrews: maxScrewsInfo(maxRowsCols, ctx.t),
    state,
    blockReason: state ? state.blocked : null,
    patch: inputPatch(input, ctx.v, catalog),
  };
}
